import React from 'react';
import { NavLink } from 'react-router-dom';
import ArticleSidebar from "../components/ArticleSidebar";
import '../style/TestPlanTemplate.css'; 
import unitTestingBanner from '../assets/unitTesting.png'; 

const UnitTestingGuide = () => {
  return (
    <section className="test-paln-wrapper">
      {/* FIXED SIDEBAR */}
      <ArticleSidebar />

      {/* SCROLLING CONTENT */}
      <article className="test-plan-container">
        <header className="entry-header">
          <h1 className="entry-title">Unit Testing - Software Testing</h1>
    
        </header>

        <div className="post-content">
          <img src={unitTestingBanner} alt="Unit Testing Banner" style={{ width: "100%", borderRadius: '12px' }} />

          <div className="table-of-contents-block">
            <div><b>Table of contents:</b></div>
            <ul>
              <li><a href="#whatIsUnitTesting">What is Unit Testing?</a></li>
              <li><a href="#whatIsAUnit">What is a Unit Test?</a></li>
              <li><a href="#strategies">Unit Testing Strategies</a></li>
              <li><a href="#example">Unit Test Example (Java & TestNG)</a></li>
              <li><a href="#benefits">Benefits of Unit Testing</a></li>
              <li><a href="#disadvantages">Disadvantages of Unit Testing</a></li>
              <li><a href="#howDevelopersUse">How do developers use unit tests?</a></li>
              <li><a href="#lessBeneficial">When is Unit Testing Less Beneficial?</a></li>
              <li><a href="#differences">Difference Between Unit Testing and other Types</a></li>
              <li><a href="#types">Types of Unit Testing (Manual vs Auto)</a></li>
              <li><a href="#workflow">Workflow of Unit Testing</a></li>
              <li><a href="#techniques">Unit Testing Techniques</a></li>
              <li><a href="#tools">Unit Testing Tools</a></li>
            </ul>
          </div>

          <section id="whatIsUnitTesting">
            <h2>Unit Testing - Software Testing</h2>
            <p>
              Unit testing is the process of testing the smallest parts of your code, like it is a method in which we verify the code's correctness by running one by one. It's a key part of software development that improves code quality by testing each unit in isolation.
            </p>
            <p>
              You write unit tests for these code units and run them automatically every time you make changes. If a test fails, it helps you quickly find and fix the issue. Unit testing promotes modular code, ensures better test coverage, and saves time by allowing developers to focus more on coding than manual testing.
            </p>
          </section>

          <section id="whatIsAUnit">
            <h2>What is a Unit Test?</h2>
            
            <p>
              A unit test is a small piece of code that checks if a specific function or method in is an application works correctly. It will work as the function inputs and verifying the outputs. These tests check that the code work as expected based on the logic the developer intended.
            </p>
            <h3>Unit Test</h3>
            <p>
              In these multiple tests are written for a single function to cover different possible scenarios and these are called test cases. While it is ideal to cover all expected behaviors, it is not always necessary to test every scenario.
            </p>
            <p>
              Unit tests should run one by one, it means that they do not depend on other system parts like databases or networks. Instead, data stubs can be used to simulate these dependencies. Writing unit tests is easiest for simple, self-contained code blocks.
            </p>
          </section>

          <section id="strategies">
            <h2>Unit testing strategies</h2>
            <p>To create effective unit tests, follow these basic techniques to ensure all scenarios are covered:</p>
            <ul>
              <li><strong>Logic checks:</strong> Verify if the system performs correct calculations and follows the expected path with valid inputs. Check all possible paths through the code are tested.</li>
              <li><strong>Boundary checks:</strong> Test how the system handles typical, edge case, and invalid inputs. For example, if an integer between 3 and 7 is expected, check how the system reacts to a 5 (normal), a 3 (edge case), and a 9 (invalid input).</li>
              <li><strong>Error handling:</strong> Check the system properly handles errors. Does it prompt for a new input, or does it crash when something goes wrong?</li>
              <li><strong>Object-oriented checks:</strong> If the code modifies objects, confirm that the object's state is correctly updated after running the code.</li>
            </ul>
          </section>

          <section id="example">
            <h2>Unit Test Example</h2>
            <p>Here is the example of the java with unit test cases with the proper unit test code.</p>
            
            <p><strong>Step 1. Create the Calculator Class.</strong></p>
            <pre className="code-block">
{`public class Calculator {
    // Method to add two numbers
    public int add(int a, int b) {
        return a + b;
    }
    // Method to subtract two numbers
    public int subtract(int a, int b) {
        return a - b;
    }
}`}
            </pre>

            <p><strong>Step 2. Create the TestNG Test Class.</strong></p>
            <pre className="code-block">
{`package com.example.tests;
import org.testng.Assert;
import org.testng.annotations.BeforeMethod;
import org.testng.annotations.Test;

public class CalculatorTest {
    private Calculator calculator;

    // This method runs before each test method
    @BeforeMethod
    public void setUp() {
        calculator = new Calculator();
    }

    // Test for the 'add' method
    @Test
    public void testAdd() {
        int result = calculator.add(5, 3);
        // Assert that the result of 5 + 3 is 8
        Assert.assertEquals(result, 8, "Addition result is incorrect");
    }

    // Test for the 'subtract' method
    @Test
    public void testSubtract() {
        int result = calculator.subtract(5, 3);
        // Assert that the result of 5 - 3 is 2
        Assert.assertEquals(result, 2, "Subtraction result is incorrect");
    }
}`}
            </pre>
            <p><strong>Output:</strong> Unit Testing Example Output</p>
            
          </section>

          <section id="benefits">
            <h2>Benefits of Unit Testing</h2>
            
            <p>Here are the Unit testing benefits which used in the software development with many ways:</p>
            <ul>
              <li><strong>Early Detection of Issues:</strong> Unit testing allows developers to detect and fix issues early in the development process before they become larger and more difficult to fix.</li>
              <li><strong>Improved Code Quality:</strong> Unit testing helps to ensure that each unit of code works as intended and meets the requirements, improving the overall quality of the software.</li>
              <li><strong>Increased Confidence:</strong> Unit testing provides developers with confidence in their code, as they can validate that each unit of the software is functioning as expected.</li>
              <li><strong>Faster Development:</strong> Unit testing enables developers to work faster and more efficiently, as they can validate changes to the code without having to wait for the full system to be tested.</li>
              <li><strong>Better Documentation:</strong> Unit testing provides clear and concise documentation of the code and its behavior, making it easier for other developers to understand and maintain the software.</li>
              <li><strong>Facilitation of Refactoring:</strong> Unit testing enables developers to safely make changes to the code, as they can validate that their changes do not break existing functionality.</li>
              <li><strong>Reduced Time and Cost:</strong> Unit testing can reduce the time and cost required for later testing, as it helps to identify and fix issues early in the development process.</li>
            </ul>
          </section>

          <section id="disadvantages">
            <h2>Disadvantages of Unit Testing</h2>
            <ul>
              <li><strong>Time and Effort:</strong> Unit testing requires a significant investment of time and effort to create and maintain the test cases, especially for complex systems.</li>
              <li><strong>Dependence on Developers:</strong> The success of unit testing depends on the developers, who must write clear, concise, and comprehensive test cases to validate the code.</li>
              <li><strong>Difficulty in Testing Complex Units:</strong> Unit testing can be challenging when dealing with complex units, as it can be difficult to isolate and test individual units in isolation from the rest of the system.</li>
              <li><strong>Difficulty in Testing Interactions:</strong> Unit testing may not be sufficient for testing interactions between units, as it only focuses on individual units.</li>
              <li><strong>Difficulty in Testing User Interfaces:</strong> Unit testing may not be suitable for testing user interfaces, as it typically focuses on the functionality of individual units.</li>
              <li><strong>Over-reliance on Automation:</strong> Over-reliance on automated unit tests can lead to a false sense of security, as automated tests may not uncover all possible issues or bugs.</li>
              <li><strong>Maintenance Overhead:</strong> Unit testing requires ongoing maintenance and updates, as the code and test cases must be kept up-to-date with changes to the software.</li>
            </ul>
          </section>

          <section id="howDevelopersUse">
            <h2>How do developers use unit tests?</h2>
            <p>Unit testing plays an important role throughout the software development process:</p>
            <ul>
              <li><strong>Test-Driven Development (TDD):</strong> In TDD, developers write tests before writing the actual code. This ensures that once the code is completed, it instantly meets the functional requirements when tested, saving time on debugging.</li>
              <li><strong>After Completing Code Blocks:</strong> After a section of code is finished, unit tests are created (if not already done through TDD). These tests are then run to verify that the code works as expected.</li>
              <li><strong>DevOps and CI/CD:</strong> In DevOps environments, Continuous Integration/Continuous Delivery (CI/CD) automatically runs unit tests whenever new code is added.</li>
            </ul>
            
          </section>

          <section id="lessBeneficial">
            <h2>When is Unit Testing Less Beneficial?</h2>
            <ul>
              <li><strong>Time Constraints:</strong> If your team is under pressure, they might get distract while writing tests and miss deadlines, leading to budget issues.</li>
              <li><strong>UI/UX Focus:</strong> If the main focus is on design rather than complex logic, manual testing might be more effective.</li>
              <li><strong>Legacy Code:</strong> Testing old code can be challenging if it was not written to be testable.</li>
              <li><strong>Changing Requirements:</strong> In projects where requirements change frequently, it may not be practical to write tests for code that will soon change.</li>
            </ul>
          </section>

          <section id="differences">
            <h2>Difference Between Unit Testing and other Types of Testing</h2>
            [Image comparing Unit, Integration, and System testing]
            <ul>
              <li><strong>Integration Testing:</strong> Checks that different parts of the system work together.</li>
              <li><strong>Functional Testing:</strong> Checks if the software meets pre-planned requirements.</li>
              <li><strong>Performance Testing:</strong> Verifies speed and memory usage.</li>
              <li><strong>Acceptance Testing:</strong> Performed manually by stakeholders to verify behavior.</li>
              <li><strong>Security Testing:</strong> Examines vulnerabilities and third-party risks.</li>
            </ul>
          </section>

          <section id="types">
            <h2>Types of Unit Testing</h2>
            <h3>1. Manual unit testing</h3>
            <p>Manual Testing is like checking each part by hand. It isn't used much because it costs more, takes a lot of time, and it is hard to test parts separately.</p>
            <h3>2. Automated unit testing</h3>
            <p>Automation Unit Testing uses special tools. Developers write a small piece of code to test a function. These test codes are taken out before the software is finished. Automated testing helps find out if one part depends too much on another.</p>
          </section>

          <section id="workflow">
            <h2>Workflow of Unit Testing</h2>
            [Image showing the step-by-step workflow of unit testing from planning to execution]
            <p>The unit testing workflow includes a step-by-step process of planning, designing test cases, executing, and fixing bugs.</p>
          </section>

          <section id="techniques">
            <h2>Unit Testing Techniques</h2>
            <ul>
              <li><strong>Black Box Testing:</strong> Covers unit tests for input, user interface, and output parts.</li>
              <li><strong>White Box Testing:</strong> Tests functional behavior, internal design structure, and code.</li>
              <li><strong>Gray Box Testing:</strong> Executes test cases and analyzes code performance.</li>
            </ul>
          </section>

          <section id="tools">
            <h2>Unit Testing Tools</h2>
            <ul>
              <li><strong>LambdaTest:</strong> Cloud-based platform for cross-browser testing.</li>
              <li><strong>JUnit & TestNG:</strong> Java testing frameworks.</li>
              <li><strong>NUnit:</strong> .NET framework for C# applications.</li>
              <li><strong>PHPUnit:</strong> PHP testing framework.</li>
              <li><strong>Mockito & TypeMock:</strong> Mocking frameworks for isolating dependencies.</li>
              <li><strong>Karma, Mocha:</strong> JavaScript testing tools.</li>
              <li><strong>Cantata:</strong> Tool for C/C++ testing.</li>
            </ul>
          </section>

          <section>
            <h2>Conclusion</h2>
            <p>Unit testing will validate individual units of software in a proper manner, checking if the function works correctly and meets the requirements of the project. While it may offer benefits such as early issue detection and improved code quality, it requires significant time and effort, which depends on the developers' skills. Checking the challenges, such as the difficulty in testing complex units and UI elements, unit testing is crucial for ensuring software quality and the longevity of the software.</p>
          </section>
        </div>

        {/* Post Metadata Footer */}
        {/* Post Metadata Footer */}
               <footer className="fusion-meta-info">
                 <div className="fusion-meta-info-wrapper">
                   By <span className="fn"><a href="#author">Manish Kumar</a></span>
                   <span className="fusion-inline-sep">|</span>
                   <span>February 28th, 2026</span>
                   <span className="fusion-inline-sep">|</span>
                   <a href="#category">QA Resources</a>
                 </div>
               </footer>
       
               {/* Sharing Box */}
               <div className="sharing-box">
                 <h4>Share This Story, Choose Your Platform!</h4>
                 <div className="fusion-social-networks">
        
           <NavLink to="https://www.linkedin.com/in/qa-coder-708418404/" target="_blank" rel="noreferrer" className="linkedin">
             <i className="fab fa-linkedin-in"></i>
           </NavLink>
           <NavLink to="https://wa.me/918299775823" target="_blank" rel="noreferrer" className="whatsapp">
             <i className="fab fa-whatsapp"></i>
           </NavLink>
           <NavLink to="mailto:qacoder0@gmail.com" className="email">
             <i className="fas fa-envelope"></i>
           </NavLink>
       {/* Instagram */}
       <NavLink to="https://www.instagram.com/qacoder0/?hl=en" target="_blank" rel="noreferrer" className="instagram">
         <i className="fab fa-instagram instagram-gradient"></i>
       </NavLink>
       
       {/* YouTube */}
       <NavLink to="https://www.youtube.com/@QACoder" target="_blank" rel="noreferrer" className="youtube">
         <i className="fab fa-youtube"></i>
       </NavLink>
       
       {/* GitHub */}
       <NavLink to="https://github.com/yourusername" target="_blank" rel="noreferrer" className="github">
         <i className="fab fa-github"></i>
       </NavLink>
       
         </div>
               </div>
      </article>
    </section>
  );
};

export default UnitTestingGuide;
